import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Grid } from 'react-feather';
import * as XLSX from 'xlsx-js-style';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable from '../shared/ReportTable';
import ReportPagination from '../shared/ReportPagination';
import ColumnPicker from '../shared/ColumnPicker';
import useColumnConfig from '../shared/useColumnConfig';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const PAGE_SIZE = 50;

const num = (v) => (v == null || v === '' ? '-' : Number(v).toLocaleString());

// Fixed (non-date) columns — only these are toggleable in the picker.
const FIXED_COLUMNS = [
    { key: 'tenant_name', label: 'Tenant Name' },
    { key: 'mall_code', label: 'Property' },
    { key: 'outlet_code', label: 'Unit Code' },
    { key: 'tenant_code', label: 'Tenant Code' },
];
const DEFAULT_COLUMNS = FIXED_COLUMNS.map((c) => c.key);

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]
    .map((y) => ({ value: String(y), label: String(y) }));

const initialFilters = {
    year: String(CURRENT_YEAR),
    property_id: '',
    selectedFloor: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const MTDReportView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions, visibleColumns: visibleFixed,
    } = useColumnConfig(FIXED_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedFloor = ''; next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            year: filters.year,
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_id: filters.selectedTenants,
            floor: filters.selectedFloor,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getmonthtodatereport`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated MTD report');
        } catch (err) {
            console.error('MTD report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate MTD report'));
        } finally {
            setLoading(false);
        }
    };

    // Sorted ISO dates across all rows → dynamic date columns.
    const dateColumns = useMemo(() => {
        const set = new Set();
        reportData.forEach((item) => {
            (item.transactions || []).forEach((txn) => {
                set.add(new Date(txn.transaction_date).toISOString().split('T')[0]);
            });
        });
        return Array.from(set).sort();
    }, [reportData]);

    // Pivot API rows → one row per tenant with a date→net map.
    const rows = useMemo(() => {
        const grouped = reportData.reduce((acc, item) => {
            const key = `${item.tenant_name}-${item.mall_code}-${item.outlet_code}-${item.tenant_code}`;
            if (!acc[key]) {
                acc[key] = {
                    tenant_name: item.tenant_name,
                    mall_code: item.mall_code,
                    outlet_code: item.outlet_code,
                    tenant_code: item.tenant_code,
                    dates: {},
                };
            }
            (item.transactions || []).forEach((txn) => {
                const date = new Date(txn.transaction_date).toISOString().split('T')[0];
                acc[key].dates[date] = { total_net_sales: txn.net_sales || 0 };
            });
            return acc;
        }, {});
        return Object.values(grouped);
    }, [reportData]);

    const sortedData = useMemo(() => {
        const data = [...rows];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [rows, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    // Final columns: visible fixed (picker) + one money col per date + Total.
    const columns = useMemo(() => {
        const dateCols = dateColumns.map((date) => ({
            key: `date__${date}`,
            label: date,
            align: 'right',
            sortable: false,
            render: (item) => num(item.dates[date]?.total_net_sales),
        }));
        const totalCol = {
            key: '__total',
            label: 'Total',
            align: 'right',
            sortable: false,
            render: (item) => {
                const t = dateColumns.reduce((s, d) => s + (item.dates[d]?.total_net_sales || 0), 0);
                return <strong>{t.toLocaleString()}</strong>;
            },
        };
        return [...visibleFixed, ...dateCols, totalCol];
    }, [visibleFixed, dateColumns]);

    const fields = [
        { key: 'year', label: t('Year'), type: 'select', options: YEAR_OPTIONS, placeholder: t('Select year') },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'selectedFloor', label: t('Floor'), type: 'select', options: lookups.floorOptions, placeholder: t('All floors') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    // Colored Excel export (zero=red, missing=yellow) — preserved from legacy.
    const exportToExcel = () => {
        if (!sortedData.length) return;
        const property_name = nameOf(lookups.propertyOptions, filters.property_id);
        const category_name = nameOf(lookups.categoryOptions, filters.category_id);
        const subcategory_name = nameOf(lookups.subcategoryOptions, filters.subcategory_id);
        const filterDetails = [
            [t('Month To Date Sales Report')],
            property_name && [`${t('Property')}: ${property_name}`],
            category_name && [`${t('Category')}: ${category_name}`],
            subcategory_name && [`${t('Subcategory')}: ${subcategory_name}`],
            filters.selectedFloor && [`${t('Floor')}: ${filters.selectedFloor}`],
            [],
        ].filter(Boolean);

        const tableData = sortedData.map((tenant) => {
            const row = [tenant.tenant_name, tenant.mall_code, tenant.outlet_code, tenant.tenant_code];
            dateColumns.forEach((date) => {
                const value = tenant.dates[date]?.total_net_sales;
                row.push(value === undefined ? '' : value);
            });
            const total = dateColumns.reduce((s, d) => s + (tenant.dates[d]?.total_net_sales || 0), 0);
            row.push(total);
            return row;
        });

        const headerRow = [t('Tenant Name'), t('Property'), t('Unit Code'), t('Tenant Code'), ...dateColumns, t('Total')];
        const worksheetData = [...filterDetails, headerRow, ...tableData];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        const startDataRow = filterDetails.length + 1;
        const monthColStart = 4;
        const monthColEnd = 4 + dateColumns.length;
        for (let R = startDataRow; R < worksheetData.length; R += 1) {
            for (let C = monthColStart; C <= monthColEnd; C += 1) {
                const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
                if (!cell) continue;
                if (typeof cell.v === 'number') {
                    cell.t = 'n';
                    cell.z = '#,##0.00';
                    if (cell.v === 0) cell.s = { fill: { patternType: 'solid', fgColor: { rgb: 'FFC7CE' } } };
                }
                if (cell.v === '' || cell.v == null) {
                    cell.s = { fill: { patternType: 'solid', fgColor: { rgb: 'FFF2CC' } } };
                }
            }
        }
        const headerRowIndex = filterDetails.length;
        for (let C = 0; C < headerRow.length; C += 1) {
            const cell = worksheet[XLSX.utils.encode_cell({ r: headerRowIndex, c: C })];
            if (cell) cell.s = { font: { bold: true } };
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MTD Report');
        XLSX.writeFile(workbook, 'MTD_Sales_Report.xlsx');
        insertToLog(' exported MTD report to excel');
    };

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('MTD Report')}
                subtitle={t('Month-to-date net sales per tenant, one column per transaction date.')}
                actions={
                    <button
                        type="button"
                        className="v3-btn v3-btn-ghost"
                        onClick={exportToExcel}
                        disabled={!sortedData.length}
                        title={t('Export to Excel')}
                    >
                        <Grid size={14} /> {t('Excel')}
                    </button>
                }
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions}
                    value={selectedColumns}
                    total={FIXED_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={columns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No data found for the selected filters. Try a different year or clearing filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('tenants')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default MTDReportView;
